import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Cookie, Settings, Check, X } from 'lucide-react';

export default function CookiePolicyPage() {
  const lastUpdated = "June 14, 2026";
  
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/" className="p-2 hover:bg-zinc-100 rounded-full transition-colors">
            <ArrowLeft className="w-5 h-5 text-zinc-700" />
          </Link>
          <div className="flex items-center gap-2">
            <Cookie className="w-5 h-5 text-zinc-700" />
            <h1 className="text-lg font-semibold text-zinc-900">Cookie Policy</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <motion.main 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto px-4 py-12"
      >
        <div className="prose prose-zinc max-w-none">
          <p className="text-sm text-zinc-500 mb-8">Last Updated: {lastUpdated}</p>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-zinc-900 mb-4">What Are Cookies?</h2>
            <p className="text-zinc-600 leading-relaxed">
              Cookies are small text files stored on your device when you visit a website. They help websites 
              remember your preferences, understand how you use the site, and provide personalized experiences. 
              AIRA ECHO uses cookies and similar technologies to improve your experience.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-zinc-900 mb-4">Types of Cookies We Use</h2>
            
            {/* Essential Cookies */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-green-900 mb-2">Essential Cookies (Required)</h3>
                  <p className="text-green-800 text-sm mb-3">
                    These cookies are necessary for the website to function. They cannot be disabled.
                  </p>
                  <ul className="text-sm text-green-700 space-y-1">
                    <li>• <strong>Authentication:</strong> Keep you logged in</li>
                    <li>• <strong>Security:</strong> Protect against fraud and abuse</li>
                    <li>• <strong>Session:</strong> Remember your actions during a visit</li>
                    <li>• <strong>Preferences:</strong> Store your language and theme settings</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Analytics Cookies */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Settings className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">Analytics Cookies (Optional)</h3>
                  <p className="text-blue-800 text-sm mb-3">
                    Help us understand how visitors interact with our website.
                  </p>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• <strong>Usage Tracking:</strong> Pages visited, time spent</li>
                    <li>• <strong>Performance:</strong> Load times, errors encountered</li>
                    <li>• <strong>Features:</strong> Which features are most popular</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Functional Cookies */}
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-6 mb-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <Settings className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-purple-900 mb-2">Functional Cookies (Optional)</h3>
                  <p className="text-purple-800 text-sm mb-3">
                    Enable enhanced functionality and personalization.
                  </p>
                  <ul className="text-sm text-purple-700 space-y-1">
                    <li>• <strong>Personalization:</strong> Remember your preferences</li>
                    <li>• <strong>Content:</strong> Tailor content recommendations</li>
                    <li>• <strong>Social:</strong> Enable social sharing features</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-zinc-900 mb-4">Cookie Details</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-zinc-200 rounded-lg overflow-hidden">
                <thead className="bg-zinc-100">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-zinc-700">Cookie Name</th>
                    <th className="px-4 py-3 text-left font-semibold text-zinc-700">Purpose</th>
                    <th className="px-4 py-3 text-left font-semibold text-zinc-700">Duration</th>
                    <th className="px-4 py-3 text-left font-semibold text-zinc-700">Type</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-200">
                  <tr>
                    <td className="px-4 py-3 text-zinc-600">aira_session</td>
                    <td className="px-4 py-3 text-zinc-600">User authentication session</td>
                    <td className="px-4 py-3 text-zinc-600">7 days</td>
                    <td className="px-4 py-3"><span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Essential</span></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-zinc-600">aira_token</td>
                    <td className="px-4 py-3 text-zinc-600">JWT authentication token</td>
                    <td className="px-4 py-3 text-zinc-600">24 hours</td>
                    <td className="px-4 py-3"><span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Essential</span></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-zinc-600">aira_theme</td>
                    <td className="px-4 py-3 text-zinc-600">Dark/light mode preference</td>
                    <td className="px-4 py-3 text-zinc-600">1 year</td>
                    <td className="px-4 py-3"><span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Essential</span></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-zinc-600">aira_analytics</td>
                    <td className="px-4 py-3 text-zinc-600">Anonymous usage analytics</td>
                    <td className="px-4 py-3 text-zinc-600">1 year</td>
                    <td className="px-4 py-3"><span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">Analytics</span></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-zinc-600">aira_consent</td>
                    <td className="px-4 py-3 text-zinc-600">Cookie consent preferences</td>
                    <td className="px-4 py-3 text-zinc-600">1 year</td>
                    <td className="px-4 py-3"><span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Essential</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-zinc-900 mb-4">Managing Cookies</h2>
            <p className="text-zinc-600 leading-relaxed mb-4">
              You can control and manage cookies in several ways:
            </p>
            <ul className="list-disc pl-6 text-zinc-600 space-y-2">
              <li><strong>Browser Settings:</strong> Most browsers allow you to refuse or delete cookies through settings</li>
              <li><strong>Cookie Banner:</strong> Use our cookie consent banner to manage preferences</li>
              <li><strong>Opt-out Links:</strong> Analytics providers offer opt-out mechanisms</li>
            </ul>
            <p className="text-zinc-600 leading-relaxed mt-4">
              <strong>Note:</strong> Disabling essential cookies may affect website functionality and prevent you from using certain features.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-zinc-900 mb-4">Browser-Specific Instructions</h2>
            <ul className="list-disc pl-6 text-zinc-600 space-y-2">
              <li><strong>Chrome:</strong> Settings → Privacy and Security → Cookies</li>
              <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies</li>
              <li><strong>Safari:</strong> Preferences → Privacy → Cookies</li>
              <li><strong>Edge:</strong> Settings → Privacy & Security → Cookies</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-zinc-900 mb-4">Third-Party Cookies</h2>
            <p className="text-zinc-600 leading-relaxed mb-4">
              We may use services that set their own cookies:
            </p>
            <ul className="list-disc pl-6 text-zinc-600 space-y-2">
              <li><strong>Stripe:</strong> For secure payment processing</li>
              <li><strong>Google:</strong> For authentication (if using Google Sign-In)</li>
            </ul>
            <p className="text-zinc-600 leading-relaxed mt-4">
              These third parties have their own privacy and cookie policies.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-zinc-900 mb-4">Updates to This Policy</h2>
            <p className="text-zinc-600 leading-relaxed">
              We may update this Cookie Policy periodically. Changes will be posted on this page with an updated 
              "Last Updated" date. We encourage you to review this policy regularly.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-zinc-900 mb-4">Contact Us</h2>
            <p className="text-zinc-600 leading-relaxed">
              If you have questions about our use of cookies, contact us at:
            </p>
            <p className="text-zinc-600 mt-2">
              <strong>Email:</strong> privacy@askaira.com
            </p>
          </section>

          {/* Footer Links */}
          <div className="mt-12 pt-8 border-t border-zinc-200 flex flex-wrap gap-4 text-sm">
            <Link to="/terms" className="text-blue-600 hover:underline">Terms of Service</Link>
            <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>
            <Link to="/" className="text-blue-600 hover:underline">Back to Home</Link>
          </div>
        </div>
      </motion.main>
    </div>
  );
}
